import {
  Formik,
  FormikConfig,
  FormikHelpers,
  FormikProps,
  useFormikContext
} from "formik"
import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react"
import isEqual from "react-fast-compare"
import { DiscardFormStateReturn } from "./useDiscardFormState"
import {
  getFormStateStorageKey,
  readStoredFormState,
  writeStoredFormState
} from "./useFormStateStorage"
import useFormikDiscardFormState, {
  FormikDiscardFormStateOptions,
  pickFormValues
} from "./useFormikDiscardFormState"

type DiscardRef = Pick<DiscardFormStateReturn, "discardFormState"> & {
  /** Latest Formik dirty flag; used to keep a draft typed after optimistic clear. */
  dirty: boolean
}

export type FormikSubmissionState = {
  /** Increments only after the async submit handler resolves successfully. */
  successfulSubmissionCount: number
}

/**
 * How form values + stored draft should behave around submit.
 *
 * - `preserve` — keep submitted values as the new baseline (edit/save forms).
 * - `clearOptimistic` — clear immediately, restore on error (chat composers).
 *   On success, keep any edits made after the optimistic clear.
 * - `clearOnSuccess` — keep values visible during submit (spinner UX); clear after success.
 */
export type FormStateOnSubmit =
  | "preserve"
  | "clearOptimistic"
  | "clearOnSuccess"

type DiscardFormStateChildren<T extends any = any> =
  | React.ReactNode
  | ((props: DiscardFormStateReturn & T) => React.ReactNode)

type FormikWithDiscardFormStateProps = {
  children: DiscardFormStateChildren<FormikProps<any> & FormikSubmissionState>
  formStateOnSubmit?: FormStateOnSubmit
} & FormikDiscardFormStateOptions &
  FormikConfig<any>

export const FormikWithDiscardFormState = forwardRef<
  DiscardRef,
  FormikWithDiscardFormStateProps
>(function FormikWithDiscardFormState(
  {
    children,
    keys,
    formStateKey,
    formStateMigrationKey,
    localStorageOptions,
    onChangeDiscardFormState,
    formStateOnSubmit = "preserve",
    // The rest are formikProps.
    onSubmit,
    ...formikProps
  },
  ref
) {
  // Null once FormikDiscardFormState unmounts, e.g. while a submit is in flight.
  const internalRef = useRef<DiscardRef | null>({} as DiscardRef)
  const [successfulSubmissionCount, setSuccessfulSubmissionCount] = useState(0)

  // Expose internalRef to the outside ref, if provided.
  useImperativeHandle(ref, () => internalRef.current!, [])

  async function handleSubmit(values: any, formikHelpers: FormikHelpers<any>) {
    const preserve = formStateOnSubmit === "preserve"
    const clearOptimistic = formStateOnSubmit === "clearOptimistic"
    const valuesOverride = preserve ? values : undefined

    // Resolved up front: the default key derives from location.pathname,
    // which may change once the form unmounts.
    const storageKey = getFormStateStorageKey(formStateKey)
    const submittedValues = pickFormValues(values, keys)
    const initialValues = pickFormValues(formikProps.initialValues, keys)

    // Without a mounted form, storage is the only record of edits made
    // after submit (flushed on unmount).
    const hasNewerStoredDraft = () => {
      const stored = readStoredFormState(storageKey, localStorageOptions)
      return Boolean(
        stored?.values && !isEqual(stored.values, submittedValues)
      )
    }

    // Optimistic composers clear before the mutation so the UI feels instant.
    if (clearOptimistic) {
      internalRef.current?.discardFormState?.(false)
    }

    try {
      const result = await onSubmit(values, formikHelpers)

      if (internalRef.current) {
        if (clearOptimistic) {
          // Already cleared before submit. Resetting again would wipe a new
          // draft typed while the request was in flight. Re-settle storage
          // only when the composer is still empty.
          if (!internalRef.current.dirty) {
            internalRef.current.discardFormState?.(false)
          }
        } else {
          internalRef.current.discardFormState?.(preserve, valuesOverride)
        }
      } else if (preserve) {
        writeStoredFormState(
          storageKey,
          undefined,
          initialValues,
          localStorageOptions
        )
      } else if (!clearOptimistic || !hasNewerStoredDraft()) {
        writeStoredFormState(storageKey, null, initialValues, localStorageOptions)
      }
      setSuccessfulSubmissionCount((count) => count + 1)

      return result
    } catch (error) {
      // Restore the submitted draft only when the composer is still empty.
      // Post-submit edits are a new draft and should not be overwritten.
      if (clearOptimistic) {
        if (internalRef.current) {
          if (!internalRef.current.dirty) {
            formikHelpers.setValues(values, true /* shouldValidate */)
          }
        } else if (!hasNewerStoredDraft()) {
          writeStoredFormState(
            storageKey,
            submittedValues,
            initialValues,
            localStorageOptions
          )
        }
      }
      throw error
    }
  }

  return (
    <Formik {...formikProps} onSubmit={handleSubmit}>
      {(form) => (
        <FormikDiscardFormState
          ref={internalRef}
          keys={keys}
          formStateKey={formStateKey}
          formStateMigrationKey={formStateMigrationKey}
          localStorageOptions={localStorageOptions}
          onChangeDiscardFormState={onChangeDiscardFormState}
        >
          {(formikFormDiscardState) =>
            typeof children === "function"
              ? children({
                  ...form,
                  ...formikFormDiscardState,
                  successfulSubmissionCount
                })
              : children
          }
        </FormikDiscardFormState>
      )}
    </Formik>
  )
})

type FormikDiscardFormStateProps = {
  children: DiscardFormStateChildren
} & FormikDiscardFormStateOptions

const FormikDiscardFormState = forwardRef<
  DiscardRef,
  FormikDiscardFormStateProps
>(function FormikDiscardFormState(
  { children, ...formikDiscardFormStateProps },
  ref
) {
  const formikFormDiscardState = useFormikDiscardFormState(
    formikDiscardFormStateProps
  )
  const { dirty } = useFormikContext()

  useImperativeHandle(ref, () => ({
    discardFormState: formikFormDiscardState.discardFormState,
    dirty
  }))

  return typeof children === "function"
    ? children(formikFormDiscardState)
    : children
})

FormikDiscardFormState.displayName = "FormikDiscardFormState"

FormikDiscardFormState.propTypes = {
  children: PropTypes.func.isRequired
}

export default FormikDiscardFormState
